import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { Mail, Phone, MapPin, Send, Calendar, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar as CalendarComponent } from '../ui/calendar';
import { format } from 'date-fns';

// The form hands the enquiry to the visitor's own mail client rather than posting it.
// Decided 2026-09-16: this removes the whole delivery tier (no Resend account, no API
// key, no DNS, no secret to rotate) at the cost of desktop conversion and of any
// visibility into whether the visitor actually pressed send.
//
// api/enquiries.js is still deployed and still works the moment RESEND_API_KEY is set,
// so this is reversible in one commit. Do NOT delete that endpoint: check-seo.mjs
// asserts POST /api/enquiries returns 400 to an invalid payload, and a 405 fails it.
const ENQUIRY_TO = 'info@newcreative.co.za';
const WHATSAPP_NUMBER = '27638060581';
const WHATSAPP_HREF = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
  "Hi New Creative Industries, I'd like to enquire about an event."
)}`;

const eventTypes = [
  'Corporate Event',
  'Wedding',
  'Concert/Festival',
  'Private Party',
  'Product Launch',
  'Other',
];

export const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [selectedDate, setSelectedDate] = useState(null);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    event_type: '',
    message: '',
    company: '', // honeypot — see the hidden input below
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEventTypeChange = (value) => {
    setFormData((prev) => ({ ...prev, event_type: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const preferredDate = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '';
    const subject = `Event enquiry from ${formData.full_name}`;
    const body = [
      `Name: ${formData.full_name}`,
      `Email: ${formData.email}`,
      `Phone: ${formData.phone}`,
      `Event type: ${formData.event_type || 'Not specified'}`,
      `Preferred date: ${preferredDate || 'Not specified'}`,
      '',
      'About the event:',
      formData.message,
      '',
      'Sent from the enquiry form on newcreative.co.za',
    ].join('\n');

    // Never claim the enquiry has been sent, because we cannot know: the visitor still
    // has to press send in their own mail app, and a desktop with no client wired to
    // mailto: does nothing visible at all. So the wording says what should have
    // happened and gives two other ways through if it did not.
    window.location.href = `mailto:${ENQUIRY_TO}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;

    toast('Your email app should now open', {
      description:
        'Everything is filled in, just press send. If nothing opened, WhatsApp us on 063 806 0581 or email info@newcreative.co.za.',
      duration: 10000,
    });
  };

  return (
    <section
      id="book"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-[#F5F8FA]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        {/* Header */}
        <div className="text-center mb-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-xs uppercase tracking-[0.3em] text-[#17798A] font-bold mb-4"
          >
            Get In Touch
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-[#111111]"
          >
            Let's Make It <span className="text-[#2A93A6]">Happen</span>
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex items-center justify-center gap-4 mt-6"
          >
            <div className="h-px w-10 bg-[#47c3d6]/50" />
            <span className="text-[#17798A] text-sm font-semibold tracking-[0.2em] uppercase">
              Book a Consultation
            </span>
            <div className="h-px w-10 bg-[#47c3d6]/50" />
          </motion.div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              {/* Full Name */}
              <div>
                <label htmlFor="contact-full-name" className="block text-[#666666] text-sm mb-2 uppercase tracking-wider">
                  Full Name
                </label>
                <input
                  id="contact-full-name"
                  type="text"
                  name="full_name"
                  autoComplete="name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  required
                  data-testid="input-name"
                  className="form-input w-full bg-transparent border-0 border-b border-[#CCCCCC] py-3 text-[#111111] placeholder-[#6B6B6B] focus:border-[#17798A] transition-colors"
                  placeholder="Your name"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="contact-email" className="block text-[#666666] text-sm mb-2 uppercase tracking-wider">
                  Email Address
                </label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  inputMode="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  data-testid="input-email"
                  className="form-input w-full bg-transparent border-0 border-b border-[#CCCCCC] py-3 text-[#111111] placeholder-[#6B6B6B] focus:border-[#17798A] transition-colors"
                  placeholder="your@email.com"
                />
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="contact-phone" className="block text-[#666666] text-sm mb-2 uppercase tracking-wider">
                  Phone Number
                </label>
                <input
                  id="contact-phone"
                  type="tel"
                  name="phone"
                  autoComplete="tel"
                  inputMode="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  data-testid="input-phone"
                  className="form-input w-full bg-transparent border-0 border-b border-[#CCCCCC] py-3 text-[#111111] placeholder-[#6B6B6B] focus:border-[#17798A] transition-colors"
                  placeholder="+27 XX XXX XXXX"
                />
              </div>

              {/* Event Type */}
              <div>
                <label
                  id="contact-event-type-label"
                  className="block text-[#666666] text-sm mb-2 uppercase tracking-wider"
                >
                  Event Type
                </label>
                <Select onValueChange={handleEventTypeChange} value={formData.event_type}>
                  <SelectTrigger
                    data-testid="select-event-type"
                    id="contact-event-type"
                    aria-labelledby="contact-event-type-label"
                    className="w-full bg-transparent border-0 border-b border-[#CCCCCC] rounded-none py-3 text-[#111111] focus:border-[#17798A] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#17798A] [&>span]:text-[#6B6B6B] [&[data-state=open]>span]:text-[#111111]"
                  >
                    <SelectValue placeholder="Select event type" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#E5E9ED]">
                    {eventTypes.map((type) => (
                      <SelectItem
                        key={type}
                        value={type}
                        className="text-[#111111] hover:bg-[#F5F8FA] focus:bg-[#F5F8FA] focus:text-[#111111] cursor-pointer"
                      >
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Preferred Date */}
              <div>
                <label
                  id="contact-preferred-date-label"
                  className="block text-[#666666] text-sm mb-2 uppercase tracking-wider"
                >
                  Preferred Date
                </label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="date-picker-trigger"
                      id="contact-preferred-date"
                      aria-labelledby="contact-preferred-date-label contact-preferred-date"
                      className="w-full flex items-center justify-between bg-transparent border-0 border-b border-[#CCCCCC] py-3 text-left focus:border-[#17798A] transition-colors"
                    >
                      <span className={selectedDate ? 'text-[#111111]' : 'text-[#6B6B6B]'}>
                        {selectedDate ? format(selectedDate, 'PPP') : 'Select a date'}
                      </span>
                      <Calendar size={18} className="text-[#6B6B6B]" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white border-[#E5E9ED]" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={(date) => date < new Date()}
                      initialFocus
                      className="bg-white text-[#111111]"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="contact-message" className="block text-[#666666] text-sm mb-2 uppercase tracking-wider">
                  Tell Us About Your Vision
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={4}
                  data-testid="input-message"
                  className="form-input w-full bg-transparent border-0 border-b border-[#CCCCCC] py-3 text-[#111111] placeholder-[#6B6B6B] focus:border-[#17798A] transition-colors resize-none"
                  placeholder="Describe your event vision, expected guest count, and any special requirements..."
                />
              </div>

              {/* Honeypot — display:none, so a person never sees or tabs into it.
                  Bots that fill every field in the DOM give themselves away. */}
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
              />

              {/* Two ways through: compose an email, or start a WhatsApp chat. */}
              <div className="flex flex-col sm:flex-row gap-3 mt-4">
                <button
                  type="submit"
                  data-testid="submit-button"
                  className="btn-primary flex-1 inline-flex items-center justify-center gap-3 px-8 py-4 text-sm font-bold uppercase tracking-widest text-white bg-[#17798A] hover:bg-[#12626F] transition-all duration-300 rounded-lg"
                >
                  Send Enquiry
                  <Send size={18} aria-hidden="true" />
                </button>

                <a
                  href={WHATSAPP_HREF}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-cta"
                  className="flex-1 inline-flex items-center justify-center gap-3 px-8 py-4 text-sm font-bold uppercase tracking-widest text-[#17798A] bg-transparent border border-[#17798A] hover:bg-[#17798A] hover:text-white transition-all duration-300 rounded-lg"
                >
                  <MessageCircle size={18} aria-hidden="true" />
                  WhatsApp Us
                </a>
              </div>
            </form>
          </motion.div>

          {/* Contact Info & Map */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="space-y-8"
          >
            {/* Contact Details */}
            <div className="space-y-6">
              <a
                href="mailto:info@newcreative.co.za"
                data-testid="contact-email"
                className="flex items-center gap-4 group"
              >
                <div className="w-14 h-14 bg-[#151515] border border-white/10 group-hover:border-[#47c3d6] rounded-xl flex items-center justify-center transition-colors">
                  <Mail size={22} className="text-[#47c3d6]" />
                </div>
                <div>
                  <p className="text-[#666666] text-xs uppercase tracking-wider mb-1">Email</p>
                  <p className="text-[#111111] group-hover:text-[#17798A] transition-colors">
                    info@newcreative.co.za
                  </p>
                </div>
              </a>

              <a
                href="tel:+27638060581"
                data-testid="contact-phone"
                className="flex items-center gap-4 group"
              >
                <div className="w-14 h-14 bg-[#151515] border border-white/10 group-hover:border-[#47c3d6] rounded-xl flex items-center justify-center transition-colors">
                  <Phone size={22} className="text-[#47c3d6]" />
                </div>
                <div>
                  <p className="text-[#666666] text-xs uppercase tracking-wider mb-1">Phone</p>
                  <p className="text-[#111111] group-hover:text-[#17798A] transition-colors">
                    063 806 0581
                  </p>
                </div>
              </a>

              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-[#151515] border border-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin size={22} className="text-[#47c3d6]" />
                </div>
                <div>
                  <p className="text-[#666666] text-xs uppercase tracking-wider mb-1">Address</p>
                  <p className="text-[#444444]">
                    244 Vonkprop Road,
                    <br />
                    Pretoria, Gauteng 0167
                  </p>
                </div>
              </div>
            </div>

            {/* Google Map */}
            <div
              className="relative h-[300px] overflow-hidden border border-white/10 rounded-xl"
              data-testid="google-map"
            >
              <iframe
                src="https://maps.google.com/maps?q=244%20Vonkprop%20Road%2C%20Pretoria%2C%20Gauteng%2C%20South%20Africa&t=&z=15&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="New Creative Industries location — 244 Vonkprop Road, Pretoria"
                className="map-dark-filter"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
